import { LemsarApiClient } from '@lemsar/api/client';
import { UI } from '@shared/models/ui';
import { StoreData } from '@shared/module/common/model/store-data';
import { Observable } from 'rxjs/Observable';
import { Injectable } from '@angular/core';
import { AdminCity, ChangePassword, CreateAdmin, CreateAdminCity, UpdateAdminCity, UpdateProfile } from '@lemsar/user/domains/models/user';
import { AuthStore } from '@lemsar/storage/stores/auth.store';
import { User } from '@lemsar/auth/domains/models/user';
import { ResponseTransformer } from '@lemsar/api/transformers/response';
import { Collection } from '@lemsar/common/domains/interface/collection';

/**
 *  @author     Arif Setianto <arifsetiantoo@gmail.com>
 *  @created    20/06/2018
 */
@Injectable()
export class UserService {
    private _ui: StoreData<UI>;

    public constructor(private api: LemsarApiClient, private authStore: AuthStore) {
        this._ui = new StoreData<UI>(new UI());
    }

    public get ui$(): Observable<UI> {
        return this._ui.stream$;
    }

    public get ui(): UI {
        return this._ui.value;
    }

    public changePassword(value: ChangePassword): Observable<User> {
        return this.api
            .patch<any>('users/' + this.authStore.user.id + '/password', value)
            .pipe(ResponseTransformer.toData(), ResponseTransformer.toSingleClass(User));
    }

    public updateProfile(value: UpdateProfile): Observable<User> {
        return this.api
            .patch<any>('users/' + this.authStore.user.id, value)
            .pipe(ResponseTransformer.toData(), ResponseTransformer.toSingleClass(User));
    }

    public fetchAllUsers(params?: any): Observable<Collection<User>> {
        const queryParams = !params ? { page: 1, limit: 20 } : Object.assign({ page: params.page || 1, limit: params.limit || 20 }, params);

        return this.api.get<any>('users', queryParams).pipe(ResponseTransformer.toCollection(User));
    }

    public blockUser(id: string): Observable<User> {
        return this.api.delete<any>('users/' + id + '/block').pipe(ResponseTransformer.toData(), ResponseTransformer.toSingleClass(User));
    }

    public unBlockUser(id: string, params: any = {}): Observable<User> {
        return this.api
            .patch<any>('users/' + id + '/unblock', params)
            .pipe(ResponseTransformer.toData(), ResponseTransformer.toSingleClass(User));
    }

    public findUser(id: string): Observable<User> {
        return this.api.get<any>('users/' + id).pipe(ResponseTransformer.toData(), ResponseTransformer.toSingleClass(User));
    }

    public updateUser(id: string, params: UpdateProfile): Observable<User> {
        return this.api.patch<any>('users/' + id, params).pipe(ResponseTransformer.toData(), ResponseTransformer.toSingleClass(User));
    }

    public fetchAdminCityUsers(params?: any): Observable<Collection<User>> {
        const queryParams = !params ? { page: 1, limit: 20 } : Object.assign({ page: params.page || 1, limit: params.limit || 20 }, params);

        return this.api.get<any>('admin-cities', queryParams).pipe(ResponseTransformer.toCollection(User));
    }

    public findAdminCity(id: string): Observable<User> {
        return this.api.get<any>('admin-cities/' + id).pipe(ResponseTransformer.toData(), ResponseTransformer.toSingleClass(User));
    }

    public createAdminCity(params: CreateAdminCity): Observable<AdminCity> {
        return this.api.post<any>('admin-cities', params).pipe(ResponseTransformer.toData(), ResponseTransformer.toSingleClass(AdminCity));
    }

    public updateAdminCity(id: string, params: UpdateAdminCity): Observable<AdminCity> {
        return this.api
            .patch<any>('admin-cities/' + id, params)
            .pipe(ResponseTransformer.toData(), ResponseTransformer.toSingleClass(AdminCity));
    }

    public deleteAdminCity(id: string): Observable<void> {
        return this.api.delete<any>('admin-cities/' + id).pipe(ResponseTransformer.toData());
    }

    public fetchAdminUsers(params?: any): Observable<Collection<User>> {
        const queryParams = !params ? { page: 1, limit: 20 } : Object.assign({ page: params.page || 1, limit: params.limit || 20 }, params);

        return this.api.get<any>('admin', queryParams).pipe(ResponseTransformer.toCollection(User));
    }

    public createAdmin(params: CreateAdmin): Observable<User> {
        return this.api.post<any>('admin', params).pipe(ResponseTransformer.toData(), ResponseTransformer.toSingleClass(User));
    }

    public deleteAdmin(id: string): Observable<void> {
        return this.api.delete<any>('admin/' + id).pipe(ResponseTransformer.toData());
    }
}



// WEBPACK FOOTER //
// ./src/lemsar/user/domains/services/user.service.ts